import { calculateTotalWeight } from '../../../components/calculator/utils/calculationUtils';
import {
  generateDocumentCommodities,
  mapDocumentToParcel,
  mapDocumentsToParcels,
} from '../../../components/calculator/utils/documentCommodityUtils';
import { ShippingService } from '../../../components/calculator/components';
import { ShippingRate } from '../../../services/shippingService';
import { ExtendedParcel, ExtendedDocument } from '../types';
import {
  fedexPackagingOptions,
  normalizeProviderCode,
  isFedexProvider,
} from '../constants/shippingOptions';

export const formatRates = (rates: ShippingRate[]): ShippingService[] =>
  rates.map((rate) => ({
    id: rate.id || `rate-${Math.random().toString(36).substring(2, 9)}`,
    name: rate.name || 'Shipping Service',
    provider: rate.provider_display_name || 'Carrier',
    providerCode: rate.provider || undefined,
    logo: '',
    price: rate.price || 0,
    currency: rate.currency || 'ILS',
    deliveryTime: rate.transit_time || '3-5 business days',
    features: [
      ...(rate.doorstep_delivery ? ['Doorstep Delivery'] : []),
      ...(rate.online_tracking ? ['Online Tracking'] : []),
      ...(rate.signature_required ? ['Signature Required'] : []),
      ...(rate.printing_required ? ['Printing Required'] : []),
      ...(rate.insurance_up_to ? [`Insured up to ${rate.insurance_up_to}`] : []),
    ],
    recommended: false,
  }));

export const calculateInsuranceCost = (value: string): string => {
  const declaredValue = parseFloat(value);
  if (isNaN(declaredValue)) return '20';
  return Math.max(declaredValue * 0.015, 20).toFixed(2);
};

export const formatDimensions = (
  packageType: string,
  parcels: ExtendedParcel[],
  documents: ExtendedDocument[]
): string => {
  if (packageType === 'package' && parcels.length > 0) {
    const parcel = parcels[0];
    return `${parcel.length} × ${parcel.width} × ${parcel.height} cm`;
  }
  if (packageType === 'document' && documents.length > 0) return 'Document (Max 0.5kg)';
  return 'Dimensions not available';
};

export const generateReferenceNumber = () =>
  'SHP-' + Math.random().toString(36).substring(2, 10).toUpperCase();

export interface BuildRatesRequestParams {
  addressData: any;
  parcels: ExtendedParcel[];
  parcelDetails: { packageType: string };
  unitSystem: string;
  selectedRateId: string | null;
  shippingRates: ShippingService[];
  currentProviderCode: string | null;
  currentProvider: string | null;
}

export const buildRatesRequest = ({
  addressData,
  parcels,
  parcelDetails,
  unitSystem,
  selectedRateId,
  shippingRates,
  currentProviderCode,
  currentProvider,
}: BuildRatesRequestParams) => {
  // Documents are tracked separately from parcels in the UI, but the rate API
  // only accepts a `parcels` array — so convert documents to parcels here.
  const isDocument =
    (parcelDetails?.packageType || addressData.shippingType) === 'document';

  const parcelsToUse = isDocument
    ? mapDocumentsToParcels(addressData.documents)
    : addressData.parcels && addressData.parcels.length > 0
      ? addressData.parcels.map((p: any) => ({
          quantity: parseInt(p.quantity) || 1,
          weight: parseFloat(p.weight) || 0,
          length: parseFloat(p.length) || 0,
          width: parseFloat(p.width) || 0,
          height: parseFloat(p.height) || 0,
          physicalWeight: parseFloat(p.weight) * (parseInt(p.quantity) || 1),
          dimensionalWeight: parseFloat(p.physicalWeight) || parseFloat(p.dimensionalWeight) || 0,
          fedexPackagingType: p.fedexPackagingType || 'YOUR_PACKAGING',
        }))
      : parcels;

  const selectedSvc = selectedRateId ? shippingRates.find((r) => r.id === selectedRateId) : null;
  const providerCode =
    selectedSvc?.providerCode ||
    addressData.selectedRateProviderCode ||
    currentProviderCode ||
    normalizeProviderCode(selectedSvc?.provider) ||
    normalizeProviderCode(addressData.selectedRateProvider) ||
    normalizeProviderCode(currentProvider);

  const origin = addressData.selectedSourceAddress?.country?.code || addressData.origin || '';
  const destination =
    addressData.selectedDestinationAddress?.country?.code || addressData.destination || '';

  const requestData: any = {
    origin,
    destination,
    originPostalCode:
      addressData.selectedSourceAddress?.zipCode || addressData.originPostalCode || '',
    destinationPostalCode:
      addressData.selectedDestinationAddress?.zipCode || addressData.destinationPostalCode || '',
    originCity: addressData.selectedSourceAddress?.city || '',
    destinationCity: addressData.selectedDestinationAddress?.city || '',
    shippingType: parcelDetails?.packageType || addressData.shippingType || 'package',
    shipmentType: addressData.shipmentType === 'international' ? 'international' : 'domestic',
    parcels: parcelsToUse.map((parcel: any) => ({
      quantity: parseFloat(parcel.quantity),
      weight: parseFloat(parcel.weight),
      length: parseFloat(parcel.length) || 0,
      width: parseFloat(parcel.width) || 0,
      height: parseFloat(parcel.height) || 0,
      physicalWeight: parseFloat(parcel.physicalWeight) || 0,
      dimensionalWeight: parseFloat(parcel.dimensionalWeight) || 0,
      fedexPackagingType: parcel.fedexPackagingType || 'YOUR_PACKAGING',
    })),
    totalWeight: calculateTotalWeight(parcelsToUse),
    unitSystem: addressData.unitSystem || unitSystem,
    isInternational: addressData.shipmentType === 'international',
    compareAllProviders: false,
  };
  if (providerCode) requestData.provider = providerCode;

  return { requestData, parcelsToUse };
};

export interface BuildPaymentDataParams {
  locationState: any;
  parcels: ExtendedParcel[];
  documents: ExtendedDocument[];
  packageType: string;
  collectionDetails: any;
  defaultShippingId: string;
  defaultShippingName: string;
  selectedRate: ShippingService;
  insuranceRequired: boolean;
  declaredValue: string;
  signatureRequired: boolean;
  shipmentPurpose: string;
  vatType: string;
  vatNumber: string;
  shippingDescription: string;
}

export const buildPaymentData = ({
  locationState,
  parcels,
  documents,
  packageType,
  collectionDetails,
  defaultShippingId,
  defaultShippingName,
  selectedRate,
  insuranceRequired,
  declaredValue,
  signatureRequired,
  shipmentPurpose,
  vatType,
  vatNumber,
  shippingDescription,
}: BuildPaymentDataParams) => {
  const isFedex = isFedexProvider(selectedRate.provider, selectedRate.providerCode);
  const insuranceCostNum =
    packageType === 'package' && insuranceRequired
      ? Number(calculateInsuranceCost(declaredValue))
      : 0;
  const taxAmount = selectedRate.features?.includes('Tax')
    ? parseFloat(
        selectedRate.features.find((f: string) => f.startsWith('Tax'))?.split(': ')[1] || '0'
      )
    : 0;

  return {
    addresses: {
      from: locationState.selectedSourceAddress,
      to: locationState.selectedDestinationAddress,
    },
    collectionDetails: {
      date: collectionDetails.date,
      timeFrom: collectionDetails.timeFrom,
      timeTo: collectionDetails.timeTo,
      instructions: collectionDetails.instructions,
    },
    default_shipping_id: defaultShippingId.trim(),
    default_shipping_name: defaultShippingName.trim(),
    parcels:
      packageType === 'package'
        ? parcels.map((parcel: ExtendedParcel) => {
            let packagingTypeDisplay = '';
            if (isFedex && parcel.fedexPackagingType) {
              const option = fedexPackagingOptions.find(
                (opt) => opt.value === parcel.fedexPackagingType
              );
              packagingTypeDisplay = option ? `${option.label} (max ${option.maxWeight}kg)` : '';
            }
            return {
              ...parcel,
              items: parcel.items || [],
              fedexPackagingType: isFedex ? parcel.fedexPackagingType : null,
              packagingTypeDisplay,
            };
          })
        : [],
    documents:
      packageType === 'document'
        ? documents.map((doc) => ({
            ...doc,
            // Give documents the same parcel shape (dimensions, physicalWeight,
            // FedEx packaging) used for rate search so label creation matches.
            ...mapDocumentToParcel(doc),
            items: generateDocumentCommodities(
              [doc],
              typeof locationState?.selectedSourceAddress?.country === 'object'
                ? locationState?.selectedSourceAddress?.country?.name
                : locationState?.selectedSourceAddress?.country ||
                  locationState?.origin ||
                  'United States'
            ),
          }))
        : [],
    selectedRate: { ...selectedRate, provider: selectedRate.providerCode || selectedRate.provider },
    price: {
      shipping: selectedRate.price,
      insurance: insuranceCostNum,
      tax: taxAmount,
      total: (selectedRate.price || 0) + insuranceCostNum + taxAmount,
    },
    parcel: {
      weight: calculateTotalWeight(parcels),
      dimensions: formatDimensions(packageType, parcels, documents),
    },
    carrier: selectedRate.providerCode || selectedRate.provider,
    service: selectedRate.name,
    deliveryEstimate: selectedRate.deliveryTime,
    currency: selectedRate.currency || 'ILS',
    isDocument: packageType === 'document',
    referenceNumber: generateReferenceNumber(),
    isFedex,
    shipmentReason: shipmentPurpose,
    signatureRequired,
    vatType,
    vatNumber,
    shippingDescription: shippingDescription.trim(),
    customsDocumentation: null,
  };
};
